import { BaseService } from './api'
import { ApiResponse } from '@/types'
import { 
  PortfolioTracker,
  PortfolioTrackerAsset,
  PortfolioTrackerAssetWithUnlock,
  PortfolioTrackerSummary,
  AllPortfoliosSummary,
  PortfolioTrackerAssetSummary,
  PortfolioGraphData,
  AssetGraphData,
  AllPortfoliosGraphData,
  CSVImportResponse,
  CSVErrorResponse,
  PortfolioListResponse,
  ProjectSearchResult
} from '@/types/portfolio'


export class PortfolioTrackerService extends BaseService {
  // Portfolio Management
  async getPortfolios(): Promise<PortfolioListResponse> {
    try {
      const response = await this.api.get<PortfolioListResponse>('/portfolio')
      return response.data || { total: 0, data: [] }
    } catch (error) {
      this.handleError(error)
      throw error
    }
  }

  async createPortfolio(name: string): Promise<PortfolioTracker> {
    try {
      const response = await this.api.post<PortfolioTracker>('/portfolio', { name })
      return response.data!
    } catch (error) {
      this.handleError(error)
      throw error
    }
  }

  async updatePortfolio(portfolioId: string, name: string): Promise<PortfolioTracker> {
    try {
      const response = await this.api.put<ApiResponse<PortfolioTracker>>(`/portfolio/${portfolioId}`, { name })
      return response.data?.data || {
        portfolio_id: portfolioId,
        name: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    } catch (error) {
      this.handleError(error)
      return {
        portfolio_id: portfolioId,
        name: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    }
  }

  async deletePortfolio(portfolioId: string): Promise<void> {
    try {
      await this.api.delete(`/portfolio/${portfolioId}`)
    } catch (error) {
      this.handleError(error)
    }
  }

  // Portfolio Summary & Analytics
  async getAllPortfoliosSummary(): Promise<AllPortfoliosSummary> {
    try {
      const response = await this.api.get<{data: AllPortfoliosSummary}>('/portfolio/summary')
      return response.data?.data || {
        total_invested_amount: 0,
        total_current_value: 0,
        total_gain_loss_usd: 0,
        total_gain_loss_percentage: 0,
        total_assets: 0
      }
    } catch (error) {
      this.handleError(error)
      throw error
    }
  }

    async getPortfolioSummary(portfolioId: string): Promise<PortfolioTrackerSummary> {
    try {
      // First try to get portfolio summary from backend (like in working project)
      try {
        const summaryResponse = await this.api.get<ApiResponse<any>>(`/portfolio/${portfolioId}/summary`)
        const summaryData = summaryResponse.data?.data
        
        if (summaryData) {
          
          // Get detailed asset data from full portfolio endpoint
          const assetsResponse = await this.api.get<ApiResponse<any>>(`/portfolio/${portfolioId}`)
          const portfolioData = assetsResponse.data?.data
          
          // Use detailed asset data
          const assets = (portfolioData?.assets || []).map((asset: {
            asset_id?: string
            project?: { project_id?: string; name?: string; logo?: string }
            project_name?: string
            logo?: string
            invested_amount?: number
            current_value?: number
            valuation?: number
            gain_loss_usd?: number
            gain_loss_percentage?: number
            valuation_source?: string
            date?: string
            created_at?: string
            terms?: string
            project_website?: string
            equity_or_tokens_amount?: number | string
          }) => {
            
            return {
              asset_id: asset.asset_id || '',
              project: {
                project_id: asset.project?.project_id || '',
                name: asset.project?.name || asset.project_name || '',
                logo: asset.project?.logo || asset.logo || ''
              },
              invested_amount: asset.invested_amount || 0,
              current_value: asset.current_value || asset.valuation || asset.invested_amount || 0,
              gain_loss_usd: asset.gain_loss_usd || 0,
              gain_loss_percentage: asset.gain_loss_percentage || 0,
              valuation_source: asset.valuation_source || 'manual',
              date: asset.date || asset.created_at || '',
              valuation: asset.valuation || 0,
              terms: asset.terms || '',
              project_website: asset.project_website || '',
              equity_or_tokens_amount: asset.equity_or_tokens_amount || 0
            }
          })

          return {
            portfolio_id: portfolioId,
            total_invested_amount: summaryData.total_invested_amount || 0,
            total_current_value: summaryData.total_current_value || 0,
            total_gain_loss_usd: summaryData.total_gain_loss_usd || 0,
            total_gain_loss_percentage: summaryData.total_gain_loss_percentage || 0,
            total_assets: assets.length,
            assets: assets
          }
        }
      } catch (summaryError) {
        // Fallback to full portfolio data
      }

      // Fallback to full portfolio data
      const response = await this.api.get<ApiResponse<any>>(`/portfolio/${portfolioId}`)
      
      const portfolioData = response.data?.data
      if (!portfolioData) {
        throw new Error('Portfolio data not found')
      }

      // Transform data to match our expected format
      const assets = (portfolioData.assets || []).map((asset: {
        asset_id?: string
        project?: { project_id?: string; name?: string; logo?: string }
        project_name?: string
        logo?: string
        invested_amount?: number
        valuation?: number
        valuation_source?: string
        date?: string
        created_at?: string
        terms?: string
        project_website?: string
        equity_or_tokens_amount?: number | string
      }) => {
        
        return {
          asset_id: asset.asset_id || '',
          project: {
            project_id: asset.project?.project_id || '',
            name: asset.project?.name || asset.project_name || '',
            logo: asset.project?.logo || asset.logo || ''
          },
          invested_amount: asset.invested_amount || 0,
          current_value: asset.valuation || asset.invested_amount || 0,
          gain_loss_usd: (asset.valuation || 0) - (asset.invested_amount || 0),
          gain_loss_percentage: asset.invested_amount ? ((asset.valuation || 0) - asset.invested_amount) / asset.invested_amount * 100 : 0,
          valuation_source: asset.valuation_source || 'manual',
          date: asset.date || asset.created_at || '',
          valuation: asset.valuation || 0,
          terms: asset.terms || '',
          project_website: asset.project_website || '',
          equity_or_tokens_amount: asset.equity_or_tokens_amount || 0
        }
      })

      // Calculate totals from assets
      const totalInvested = assets.reduce((sum: number, asset: { invested_amount?: number }) => sum + (asset.invested_amount || 0), 0)
      const totalCurrentValue = assets.reduce((sum: number, asset: { current_value?: number }) => sum + (asset.current_value || 0), 0)
      const totalGainLoss = totalCurrentValue - totalInvested
      const totalGainLossPercentage = totalInvested > 0 ? (totalGainLoss / totalInvested) * 100 : 0

      return {
        portfolio_id: portfolioId,
        total_invested_amount: totalInvested,
        total_current_value: totalCurrentValue,
        total_gain_loss_usd: totalGainLoss,
        total_gain_loss_percentage: totalGainLossPercentage,
        total_assets: assets.length,
        assets: assets
      }


    } catch (error) {
      this.handleError(error)
      return {
        portfolio_id: portfolioId,
        total_invested_amount: 0,
        total_current_value: 0,
        total_gain_loss_usd: 0,
        total_gain_loss_percentage: 0,
        total_assets: 0,
        assets: []
      }
    }
  }

  async getAssetSummary(assetId: string): Promise<PortfolioTrackerAssetSummary> {
    try {
      const response = await this.api.get<ApiResponse<PortfolioTrackerAssetSummary>>(`/portfolio/assets/${assetId}/summary`)
      return response.data?.data || {
        asset_id: assetId,
        project: {
          project_id: '',
          name: '',
          logo: ''
        },
        invested_amount: 0,
        current_value: 0,
        project_valuation: 0,
        previous_valuation: 0,
        gain_loss_usd: 0,
        gain_loss_percentage: 0,
        valuation_source: 'manual',
        date: new Date().toISOString(),
        project_website: ''
      }
    } catch (error) {
      this.handleError(error)
      return {
        asset_id: assetId,
        project: {
          project_id: '',
          name: '',
          logo: ''
        },
        invested_amount: 0,
        current_value: 0,
        project_valuation: 0,
        previous_valuation: 0,
        gain_loss_usd: 0,
        gain_loss_percentage: 0,
        valuation_source: 'manual',
        date: new Date().toISOString(),
        project_website: ''
      }
    }
  }

  // Performance Graphs
  async getAllPortfoliosGraph(): Promise<AllPortfoliosGraphData> {
    try {
      const response = await this.api.get<ApiResponse<AllPortfoliosGraphData>>('/portfolio/summary/graph')
      // ApiClient wraps the API payload as { success: true, data: { monthly_values: [...] } }
      // so response.data?.data is the AllPortfoliosGraphData
      const graphData = response.data?.data || { monthly_values: [] }

      return graphData
    } catch (error) {
      this.handleError(error)
      return {
        monthly_values: []
      }
    }
  }

  async getPortfolioGraph(portfolioId: string): Promise<PortfolioGraphData> {
    try {
      const response = await this.api.get<ApiResponse<PortfolioGraphData>>(`/portfolio/${portfolioId}/summary/graph`)
      return response.data?.data || {
        portfolio_id: portfolioId,
        monthly_values: []
      }
    } catch (error) {
      this.handleError(error)
      return {
        portfolio_id: portfolioId,
        monthly_values: []
      }
    }
  }

  async getAssetGraph(assetId: string): Promise<AssetGraphData> {
    try {
      const response = await this.api.get<ApiResponse<AssetGraphData>>(`/portfolio/assets/${assetId}/summary/graph`)
      return response.data?.data || {
        asset_id: assetId,
        project: {
          project_id: '',
          name: '',
          logo: ''
        },
        monthly_values: []
      }
    } catch (error) {
      this.handleError(error)
      return {
        asset_id: assetId,
        project: {
          project_id: '',
          name: '',
          logo: ''
        },
        monthly_values: []
      }
    }
  }

  // Asset Management
  async getPortfolioAssets(portfolioId: string): Promise<PortfolioTrackerAsset[]> {
    try {
      const response = await this.api.get<ApiResponse<PortfolioTrackerAsset[]>>(`/portfolio/${portfolioId}/assets`)
      return response.data?.data || []
    } catch (error) {
      this.handleError(error)
      return []
    }
  }

  async createAsset(portfolioId: string, assetData: {
    project_name: string
    project_website?: string
    date: string
    invested_amount: number
    valuation: number
    equity_or_tokens_amount: number
    terms: string
  }): Promise<PortfolioTrackerAsset> {
    try {
      const response = await this.api.post<ApiResponse<PortfolioTrackerAsset>>(`/portfolio/${portfolioId}/assets`, assetData)
      return response.data?.data || {
        asset_id: '',
        portfolio_id: portfolioId,
        project_name: assetData.project_name,
        project_website: assetData.project_website || '',
        invested_amount: assetData.invested_amount,
        valuation: assetData.valuation,
        equity_or_tokens_amount: assetData.equity_or_tokens_amount,
        terms: assetData.terms,
        date: assetData.date,
        project_id: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    } catch (error) {
      this.handleError(error)
      return {
        asset_id: '',
        portfolio_id: portfolioId,
        project_name: assetData.project_name,
        project_website: assetData.project_website || '',
        invested_amount: assetData.invested_amount,
        valuation: assetData.valuation,
        equity_or_tokens_amount: assetData.equity_or_tokens_amount,
        terms: assetData.terms,
        date: assetData.date,
        project_id: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    }
  }

  async getAssetDetails(assetId: string): Promise<PortfolioTrackerAssetWithUnlock> {
    try {
      const response = await this.api.get<ApiResponse<PortfolioTrackerAssetWithUnlock>>(`/portfolio/assets/${assetId}`)
      return response.data?.data || {
        asset_id: assetId,
        portfolio_id: '',
        project_name: '',
        project: {
          project_id: '',
          name: '',
          logo: ''
        },
        project_website: '',
        invested_amount: 0,
        valuation: 0,
        equity_or_tokens_amount: 0,
        terms: '',
        date: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        unlock: undefined
      }
    } catch (error) {
      this.handleError(error)
      return {
        asset_id: assetId,
        portfolio_id: '',
        project_name: '',
        project: {
          project_id: '',
          name: '',
          logo: ''
        },
        project_website: '',
        invested_amount: 0,
        valuation: 0,
        equity_or_tokens_amount: 0,
        terms: '',
        date: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        unlock: undefined
      }
    }
  }

  async updateAsset(assetId: string, assetData: {
    project_name: string
    project_website?: string
    date: string
    invested_amount: number
    valuation: number
    equity_or_tokens_amount: number
    terms: string
  }): Promise<PortfolioTrackerAsset> {
    try {
      const response = await this.api.put<ApiResponse<PortfolioTrackerAsset>>(`/portfolio/assets/${assetId}`, assetData)
      return response.data?.data || {
        asset_id: assetId,
        portfolio_id: '',
        project_name: assetData.project_name,
        project_website: assetData.project_website || '',
        invested_amount: assetData.invested_amount,
        valuation: assetData.valuation,
        equity_or_tokens_amount: assetData.equity_or_tokens_amount,
        terms: assetData.terms,
        date: assetData.date,
        project_id: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    } catch (error) {
      this.handleError(error)
      return {
        asset_id: assetId,
        portfolio_id: '',
        project_name: assetData.project_name,
        project_website: assetData.project_website || '',
        invested_amount: assetData.invested_amount,
        valuation: assetData.valuation,
        equity_or_tokens_amount: assetData.equity_or_tokens_amount,
        terms: assetData.terms,
        date: assetData.date,
        project_id: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    }
  }

  async deleteAsset(assetId: string): Promise<void> {
    try {
      await this.api.delete(`/portfolio/assets/${assetId}`)
    } catch (error) {
      this.handleError(error)
    }
  }

  // CSV Import/Export
  async createPortfolioFromCSV(portfolioName: string, csvFile: File): Promise<CSVImportResponse> {
    try {
      const formData = new FormData()
      formData.append('portfolio_name', portfolioName)
      formData.append('csv_file', csvFile)

      const response = await this.api.post<ApiResponse<CSVImportResponse>>('/portfolio/csv', formData, {
        'Content-Type': 'multipart/form-data'
      })
      return response.data?.data || {
        portfolio_id: '',
        name: portfolioName,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        matched_assets: 0,
        assets: []
      }
    } catch (error) {
      this.handleError(error)
      return {
        portfolio_id: '',
        name: portfolioName,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        matched_assets: 0,
        assets: []
      }
    }
  }

  async addAssetsFromCSV(portfolioId: string, csvFile: File): Promise<CSVImportResponse> {
    try {
      const formData = new FormData()
      formData.append('csv_file', csvFile)

      const response = await this.api.post<ApiResponse<CSVImportResponse>>(`/portfolio/${portfolioId}/assets/csv`, formData, {
        'Content-Type': 'multipart/form-data'
      })
      return response.data?.data || {
        portfolio_id: portfolioId,
        name: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        matched_assets: 0,
        assets: []
      }
    } catch (error) {
      this.handleError(error)
      return {
        portfolio_id: portfolioId,
        name: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        matched_assets: 0,
        assets: []
      }
    }
  }

  // Helper method to handle CSV errors
  isCSVError(error: any): error is CSVErrorResponse {
    return error && error.error && error.valid_columns && error.invalid_columns && error.required_columns
  }

  // Search projects for portfolio assets
  async searchProjects(query: string): Promise<ProjectSearchResult[]> {
    try {
      const response = await this.api.get<ApiResponse<ProjectSearchResult[]>>('/projects/ids', { 
        params: { 
          search: query,
          include_logo: true 
        } 
      })
      return response.data?.data || []
    } catch (error) {
      this.handleError(error)
      return []
    }
  }
}

export const portfolioTrackerService = new PortfolioTrackerService()
